import { useRef } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import {
  DECOR_PICTURE_LABELS,
  DECOR_PICTURE_TYPES,
  MAX_DECOR_PICTURES_PER_KIND,
  type DecorPictureKind,
  type EntityAttachment,
} from '@peles/shared';
import { useDialog } from '../../../context/DialogContext';

/** What each group of pictures is used for — said beside it, so nobody guesses. */
const HINTS: Record<DecorPictureKind, string> = {
  chairs: 'The chairs’ frame and cushion colours come from these.',
  flowers: 'The arrangements take their flower and greenery colours from these.',
  hall: 'The walls are read from the top of these, the floor from the bottom.',
  tableware: 'The plates and chargers take their colours from these.',
};

export interface DecorPictureGroupProps {
  kind: DecorPictureKind;
  pictures: EntityAttachment[];
  busy: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (picture: EntityAttachment) => void;
  /** The colours these pictures gave, shown under them. */
  colors?: string[];
}

/** One group of the event's decor pictures: add, look at, take out. */
export function DecorPictureGroup({
  kind,
  pictures,
  busy,
  onAdd,
  onRemove,
  colors = [],
}: DecorPictureGroupProps) {
  const input = useRef<HTMLInputElement>(null);
  const { confirm } = useDialog();
  const label = DECOR_PICTURE_LABELS[kind];
  const full = pictures.length >= MAX_DECOR_PICTURES_PER_KIND;

  const remove = async (picture: EntityAttachment) => {
    const ok = await confirm({
      title: 'Remove this picture?',
      message: `${picture.fileName ?? 'The picture'} is taken out of ${label.toLowerCase()}, and its colours with it.`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (ok) onRemove(picture);
  };

  return (
    <div className="space-y-2" data-testid={`decor-pictures-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700">
          Pictures{' '}
          <span className="font-normal text-gray-400">
            ({pictures.length}/{MAX_DECOR_PICTURES_PER_KIND})
          </span>
        </p>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Working" />}
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {pictures.map((p) => (
          <figure
            key={p.id}
            className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100"
          >
            {p.downloadUrl ? (
              <img
                src={p.downloadUrl}
                alt={p.fileName ?? label}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-gray-400">
                {p.fileName}
              </div>
            )}
            <button
              type="button"
              onClick={() => void remove(p)}
              disabled={busy}
              aria-label={`Remove ${p.fileName ?? 'picture'}`}
              className="absolute right-1 top-1 flex min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </figure>
        ))}
        {!full && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={busy}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-50"
          >
            <ImagePlus className="h-5 w-5" />
            Add
          </button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        multiple
        accept={DECOR_PICTURE_TYPES.join(',')}
        className="hidden"
        aria-label={`Add pictures of ${label.toLowerCase()}`}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onAdd(files);
        }}
      />
      <p className="text-xs text-gray-500">{HINTS[kind]}</p>
      {colors.length > 0 && (
        <div className="flex items-center gap-1" aria-label={`Colours from ${label.toLowerCase()}`}>
          {colors.map((c) => (
            <span
              key={c}
              title={c}
              className="h-4 w-4 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
